import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { services } from '@/mocks/services';

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const featured = services.slice(0, 6);

  return (
    <section ref={ref} className="section-padding bg-[#0d0d0d] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent"></div>
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-[#d4af37]/3 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#d4af37]"></span>
            <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">What We Do</span>
            <span className="w-8 h-px bg-[#d4af37]"></span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            World-Class <span className="text-[#d4af37]">Music Services</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            From recording to distribution, from production to promotion — we offer end-to-end music industry services under one roof.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {featured.map((svc, i) => (
            <Link
              key={svc.slug}
              to={`/services/${svc.slug}`}
              className={`group glass-card rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 hover:border-[#d4af37]/40 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="h-44 overflow-hidden relative">
                <img
                  alt={`${svc.title} - Ganga Digital service`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  src={svc.image}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center rounded-xl bg-[#d4af37]/20 border border-[#d4af37]/30">
                  <i className={`${svc.icon} text-[#d4af37] text-lg`}></i>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-[#d4af37] transition-colors">{svc.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">{svc.shortDesc}</p>
                <div className="flex items-center gap-2 text-[#d4af37] text-sm font-medium">
                  Learn More <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className={`text-center transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link to="/services" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105">
            View All 15 Services <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
